import React from 'react';
import Link from 'next/link';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/twUtils';

// Le CTA non si sollevano all'hover: cambiano tinta e ombra, restano ferme.
const ctaVariants = cva(
  'ds-focus inline-flex cursor-pointer items-center justify-center gap-2 rounded-ds font-semibold text-center ' +
    'transition-[background-color,border-color,box-shadow] duration-150 motion-reduce:transition-none',
  {
    variants: {
      variant: {
        /** CTA primaria: una sola per sezione */
        accent:
          'bg-accent text-white shadow-[0_10px_26px_rgb(234_88_12/0.28)] hover:bg-accent-600 hover:shadow-[0_14px_30px_rgb(234_88_12/0.34)]',
        /** CTA secondaria su fondo chiaro */
        outline:
          'bg-white/70 text-primary border-[1.5px] border-edge hover:bg-white hover:border-edge-strong',
        /** CTA secondaria su fondo navy */
        onDark: 'text-white border-[1.5px] border-white/28 hover:bg-white/10',
        /** Navy pieno: usata solo nella barra di navigazione */
        navy: 'bg-primary text-white hover:bg-accent',
      },
      size: {
        lg: 'text-base px-7 py-4',
        md: 'text-[0.9375rem] px-[1.375rem] py-[0.8125rem]',
        sm: 'text-[0.90625rem] px-5 py-[0.6875rem]',
      },
      block: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: { variant: 'accent', size: 'lg', block: false },
  }
);

type CtaVariantProps = VariantProps<typeof ctaVariants>;

export type CtaButtonProps = CtaVariantProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
    /** Se presente rende un link, altrimenti un <button> */
    href?: string;
    onClick?: React.MouseEventHandler<HTMLElement>;
    type?: 'button' | 'submit';
    /** Solo sulla forma <button>: form incompleti, invio in corso */
    disabled?: boolean;
  };

/**
 * Call to action del design system. `variant` sceglie il peso visivo:
 * per la regola visiva del sito resta una sola `accent` per sezione.
 */
const CtaButton = ({
  href,
  variant,
  size,
  block,
  className,
  type = 'button',
  disabled = false,
  ...props
}: CtaButtonProps) => {
  const classes = cn(
    ctaVariants({ variant, size, block }),
    disabled && 'cursor-not-allowed opacity-45 hover:shadow-none',
    className
  );

  if (!href) {
    return (
      <button
        type={type}
        disabled={disabled}
        className={classes}
        {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      />
    );
  }

  // I link esterni (app, signup) escono dal router: usiamo <a> nativo
  // Anche `mailto:` è fuori dal router: un `<Link>` lo aprirebbe, ma prefetch e
  // scroll non hanno senso su un indirizzo di posta
  const isExternal = /^(https?:\/\/|mailto:)/.test(href);
  if (isExternal) return <a href={href} className={classes} {...props} />;

  return <Link href={href} className={classes} {...props} />;
};

export default CtaButton;
export { ctaVariants };
